/**
 * PlayNowPanel for Poker Reborn
 *
 * Expandable panel with 3 "Play Now" sub-option buttons:
 * - Spectator (watch only)
 * - Auto Buy-In (auto-seated with default chips)
 * - Auto-Seat (seated, manual buy-in at table)
 *
 * Appears below the "Play Now" button on a GameTypeCard when expanded.
 *
 * @see docs/screens/lobby/PLAY_NOW_PANEL.md - Visual specs, dimensions, behavior
 * @see src/components/lobby/GameTypeCard.ts - Parent card component
 * @see src/config/lobby.ts - Default buy-in multiplier
 */

import { Container, Graphics } from 'pixi.js';
import { Button } from '@/components/common/Button';
import type { ButtonStyle } from '@/components/common/Button';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Panel background */
  panel: {
    color: 0x0e0e1a,
    alpha: 0.9,
    cornerRadius: 8,
    padding: 8,
  },

  /** Button dimensions */
  button: {
    height: 32,
    fontSize: 12,
    gap: 6,
  },

  /** Spectator button (dark) */
  spectator: {
    backgroundColor: 0x151520,
    hoverColor: 0x252535,
    pressColor: 0x0a0a14,
    borderColor: 0x2a2a40,
    textColor: 0x9a9ab0,
    cornerRadius: 6,
    fontSize: 12,
  } satisfies Partial<ButtonStyle>,

  /** Auto Buy-In button (gold) */
  autoBuyIn: {
    backgroundColor: 0xa48f17,
    hoverColor: 0xd4af37,
    pressColor: 0x846f07,
    borderColor: 0xd4af37,
    textColor: 0x000000,
    cornerRadius: 6,
    fontSize: 12,
  } satisfies Partial<ButtonStyle>,

  /** Auto-Seat button (green) */
  autoSeat: {
    backgroundColor: 0x2a3a5e,
    hoverColor: 0x3a4a7e,
    pressColor: 0x1a2a4e,
    borderColor: 0x4a5a8e,
    textColor: 0xffffff,
    cornerRadius: 6,
    fontSize: 12,
  } satisfies Partial<ButtonStyle>,
};

// =============================================================================
// PLAY NOW PANEL
// =============================================================================

export class PlayNowPanel extends Container {
  private background: Graphics;
  private spectatorButton: Button;
  private autoBuyInButton: Button;
  private autoSeatButton: Button;

  /** Callback when Spectator is clicked */
  onSpectator?: () => void;
  /** Callback when Auto Buy-In is clicked */
  onAutoBuyIn?: () => void;
  /** Callback when Auto-Seat is clicked */
  onAutoSeat?: () => void;

  private panelWidth: number;

  constructor(width: number) {
    super();

    this.panelWidth = width;
    const buttonWidth = width - STYLES.panel.padding * 2;
    const { height: buttonHeight, gap } = STYLES.button;

    // Background
    this.background = new Graphics();
    this.addChild(this.background);

    // Spectator button
    this.spectatorButton = new Button({
      label: 'Spectator',
      width: buttonWidth,
      height: buttonHeight,
      style: STYLES.spectator,
    });
    this.spectatorButton.x = STYLES.panel.padding;
    this.spectatorButton.y = STYLES.panel.padding;
    this.spectatorButton.onClick = () => this.onSpectator?.();
    this.addChild(this.spectatorButton);

    // Auto Buy-In button
    this.autoBuyInButton = new Button({
      label: 'Auto Buy-In',
      width: buttonWidth,
      height: buttonHeight,
      style: STYLES.autoBuyIn,
    });
    this.autoBuyInButton.x = STYLES.panel.padding;
    this.autoBuyInButton.y = STYLES.panel.padding + buttonHeight + gap;
    this.autoBuyInButton.onClick = () => this.onAutoBuyIn?.();
    this.addChild(this.autoBuyInButton);

    // Auto-Seat button
    this.autoSeatButton = new Button({
      label: 'Auto-Seat',
      width: buttonWidth,
      height: buttonHeight,
      style: STYLES.autoSeat,
    });
    this.autoSeatButton.x = STYLES.panel.padding;
    this.autoSeatButton.y = STYLES.panel.padding + (buttonHeight + gap) * 2;
    this.autoSeatButton.onClick = () => this.onAutoSeat?.();
    this.addChild(this.autoSeatButton);

    // Draw background
    this.drawBackground();
  }

  /** Total height of the panel */
  get panelHeight(): number {
    const { height: buttonHeight, gap } = STYLES.button;
    return STYLES.panel.padding * 2 + buttonHeight * 3 + gap * 2;
  }

  /** Resize panel width */
  resizeWidth(width: number): void {
    this.panelWidth = width;
    const buttonWidth = width - STYLES.panel.padding * 2;
    const { height: buttonHeight, gap } = STYLES.button;

    this.spectatorButton.resize(buttonWidth, buttonHeight);
    this.spectatorButton.x = STYLES.panel.padding;

    this.autoBuyInButton.resize(buttonWidth, buttonHeight);
    this.autoBuyInButton.x = STYLES.panel.padding;

    this.autoSeatButton.resize(buttonWidth, buttonHeight);
    this.autoSeatButton.x = STYLES.panel.padding;

    // Reposition
    this.spectatorButton.y = STYLES.panel.padding;
    this.autoBuyInButton.y = STYLES.panel.padding + buttonHeight + gap;
    this.autoSeatButton.y = STYLES.panel.padding + (buttonHeight + gap) * 2;

    this.drawBackground();
  }

  private drawBackground(): void {
    const { color, alpha, cornerRadius } = STYLES.panel;
    this.background.clear();
    this.background.roundRect(0, 0, this.panelWidth, this.panelHeight, cornerRadius);
    this.background.fill({ color, alpha });
  }
}
